import { Box, Grid, SimpleGrid } from '@mantine/core';
import Section from './infoSection';
import InfoRow from '../../../Common/ui/InfoRow';
import { useState } from 'react';
import { formatMultilineText } from '../../../../../../../shared/utils/formatText';
import { formatDate } from '../../../../../../../shared/utils/time';
import { AS_CS_RECEIPT_TYPE_LABELS } from '../../../../../../../shared/constants/asCsOptions';
import { IAsCsReceiptDetail } from '../types';

type SectionKey = 'basic' | 'reception';

interface IReceiptInfoSectionsProps {
  detail?: IAsCsReceiptDetail;
}

export default function ReceiptInfoSection(props: IReceiptInfoSectionsProps) {
  const { detail } = props;
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    basic: false,
    reception: false,
  });
  const [sectionTruncationMap, setSectionTruncationMap] = useState<Record<SectionKey, Record<string, boolean>>>({
    basic: {},
    reception: {},
  });

  const handleToggleSection = (section: SectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleTruncationChange = (section: SectionKey, fieldKey: string, isTruncated: boolean) => {
    setSectionTruncationMap((prev) => {
      if (prev[section][fieldKey] === isTruncated) {
        return prev;
      }

      return {
        ...prev,
        [section]: {
          ...prev[section],
          [fieldKey]: isTruncated,
        },
      };
    });
  };

  const isSectionExpandable = (section: SectionKey) => Object.values(sectionTruncationMap[section]).some(Boolean);
  const shouldShowSectionToggle = (section: SectionKey) => expandedSections[section] || isSectionExpandable(section);
  return (
    <SimpleGrid cols={{ base: 1, lg: 1 }} spacing="md" style={{ alignItems: 'stretch' }}>
      <Section
        title="기본 정보"
        expanded={expandedSections.basic}
        showToggle={shouldShowSectionToggle('basic')}
        onToggle={() => handleToggleSection('basic')}
        expandLabel="전체 보기"
        collapseLabel="간단히 보기"
      >
        <Box flex={1} px="xs">
          {/* tooltip props are temporarily disabled */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <InfoRow
              label="거래처명"
              value={formatMultilineText(detail?.customerName)}
              clamp={2}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'customerName', isTruncated)}
            />
            <InfoRow
              label="주소"
              value={formatMultilineText(detail?.customerAddress?.address || '-')}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'address', isTruncated)}
            />
            <InfoRow
              label="담당자"
              value={formatMultilineText(detail?.customerManager)}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'contactPerson', isTruncated)}
            />
            <InfoRow
              label="연락처"
              value={formatMultilineText(detail?.customerPhone)}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'phoneNumber', isTruncated)}
            />
            <InfoRow
              label="모델(모델코드)"
              value={formatMultilineText(`${detail?.modelName}(${detail?.model})`)}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'machineModel', isTruncated)}
            />
            <InfoRow
              label="기계장비S/N"
              value={formatMultilineText(detail?.serialNumber)}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'machineSerialNumber', isTruncated)}
            />
            <InfoRow
              label="출고일"
              value={formatDate(detail?.equipmentRegDt)}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'shipmentDate', isTruncated)}
            />
            <InfoRow
              label="기계장비 위치"
              value={formatMultilineText(detail?.equipmentLocation?.address || '-')}
              clamp={2}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'machineLocation', isTruncated)}
            />
            <InfoRow
              label="제조번호"
              value={formatMultilineText(detail?.lotNumber)}
              clamp={1}
              expanded={expandedSections.basic}
              onTruncationChange={(isTruncated) => handleTruncationChange('basic', 'manufacturingNumber', isTruncated)}
            />
          </SimpleGrid>
        </Box>
      </Section>

      <Section
        title="AS/CS 접수정보"
        expanded={expandedSections.reception}
        showToggle={shouldShowSectionToggle('reception')}
        onToggle={() => handleToggleSection('reception')}
        expandLabel="전체 보기"
        collapseLabel="간단히 보기"
      >
        <Box flex={1} px="xs">
          {/* tooltip props are temporarily disabled */}
          <Grid gutter="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <InfoRow
                label="관리번호"
                value={formatMultilineText(String(detail?.manageNo))}
                clamp={1}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'managementNumber', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <InfoRow
                label="접수번호"
                value={formatMultilineText(detail?.receiptNo)}
                clamp={1}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'receiptNumber', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <InfoRow
                label="접수일자"
                value={formatDate(detail?.receiptDt)}
                clamp={1}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'receivedDate', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <InfoRow
                label="접수 유형"
                value={AS_CS_RECEIPT_TYPE_LABELS[detail?.receiptType as keyof typeof AS_CS_RECEIPT_TYPE_LABELS] ?? '-'}
                clamp={1}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'receptionType', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <InfoRow
                label="접수 담당자"
                value={formatMultilineText(detail?.receiptManager)}
                clamp={1}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'receiptManager', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <InfoRow
                label="에러 코드"
                value={formatMultilineText(detail?.errorCode)}
                clamp={1}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'errorCode', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <InfoRow
                label="접수 내용"
                value={formatMultilineText(detail?.receiptContent)}
                clamp={2}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'requestDetails', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <InfoRow
                label="대응 내용"
                value={formatMultilineText(detail?.receiptResult)}
                clamp={2}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'responseDetails', isTruncated)}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <InfoRow
                label="접수 메모"
                value={formatMultilineText(detail?.receiptNote)}
                clamp={2}
                expanded={expandedSections.reception}
                onTruncationChange={(isTruncated) => handleTruncationChange('reception', 'receptionMemo', isTruncated)}
              />
            </Grid.Col>
          </Grid>
        </Box>
      </Section>
    </SimpleGrid>
  );
}
